import { z } from 'zod';
/**
 * Contrats des LIENS DE PARTAGE courts (XFILE-05, -06 ; ADR-0005) — source unique
 * front + back.
 *
 * DEUX cibles de partage : un FICHIER (`/f/<slug>`) ou un DOSSIER (`/d/<slug>`). Le
 * slug est un identifiant BASE62 de 10 caractères (~59 bits) : court, non devinable
 * (CSPRNG, jamais un compteur séquentiel — la GÉNÉRATION est côté serveur). Le lien
 * n'a PAS d'expiration propre : il vit exactement le temps du fichier/dossier
 * sous-jacent — à l'expiration (TTL/GC) ou au soft-delete, la résolution renvoie
 * `410 Gone` (`ErrorCode.GONE`) ; un slug inexistant renvoie `404`.
 *
 * Les routes PUBLIQUES de résolution/contenu (`/api/share/*`) ne sont PAS
 * authentifiées (accès direct sans login) ; la clé de stockage interne n'est JAMAIS
 * exposée.
 */
/** Alphabet base62 des slugs (chiffres + lettres, sans séparateur ni caractère ambigu retiré). */
export const SHARE_SLUG_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
/** Longueur d'un slug de partage (10 car. base62 ≈ 59 bits d'entropie). */
export const SHARE_SLUG_LENGTH = 10;
/** Un slug de partage VALIDE : exactement 10 caractères base62. */
export const shareSlugSchema = z
    .string()
    .regex(/^[0-9A-Za-z]{10}$/, 'Slug de partage invalide');
/** Un identifiant est-il un slug de partage plausible ? (garde de route SPA). */
export function isShareSlug(value) {
    return shareSlugSchema.safeParse(value).success;
}
/**
 * Nature de la cible d'un lien de partage. Aligné sur l'enum PostgreSQL
 * `share_target` (`0005_share_links.sql`).
 */
export const shareTargetKindSchema = z.enum(['file', 'folder']);
/**
 * Un lien de partage résolu (renvoyé par la création/ensure et l'upload).
 *
 * `imageEmbeddable` (optionnel) indique que la cible est un lien de FICHIER dont le
 * contenu est une IMAGE : l'écran de dépôt peut alors proposer un bouton « copier le lien
 * [img] » (BBCode `[img]<url>[/img]`, forums). La valeur est calculée côté serveur au
 * dépôt (MIME réel par magic bytes ET réglage `bbcodeImageLinkEnabled` actif) ; absente
 * ou `false` ⇒ pas de bouton [img]. Un lien de DOSSIER ne l'a jamais.
 */
export const shareLinkSchema = z.object({
    slug: z.string(),
    targetKind: shareTargetKindSchema,
    imageEmbeddable: z.boolean().optional(),
    /**
     * Horodatage de RÉVOCATION du lien (XFILE-39), `null`/absent = lien ACTIF. **Renseigné par le
     * LOT 3** ; la colonne existe depuis `0020_share_revocation.sql`.
     *
     * OPTIONNEL, donc additif : les producteurs actuels (création/ensure, résultat d'upload) ne
     * renvoient QUE des liens actifs et continuent de l'omettre — rien à changer chez eux. Le champ
     * n'existe que pour les surfaces qui montrent l'HISTORIQUE d'une cible (« ce fichier a été publié,
     * puis dépublié le … »), où l'absence d'information et « jamais partagé » ne se distinguent pas.
     */
    revokedAt: z.string().nullable().optional(),
});
/**
 * Corps de `POST /api/share` (écriture) : garantit (idempotent) un lien de partage
 * pour une cible existante et active (fichier ou dossier), désignée par son id.
 */
export const createShareRequestSchema = z.object({
    targetKind: shareTargetKindSchema,
    targetId: z.uuid(),
});
// ---------------------------------------------------------------------------
// LOT 1 — RÉVOCATION D'UN LIEN DE PARTAGE (XFILE-39). Contrats posés, **consommés par le LOT 3**.
// Migration `0020_share_revocation.sql` pose la colonne `share_links.revoked_at`.
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/share/revoke` — RETIRE la publication d'une cible, sans toucher à la donnée.
 *
 * MANQUE COMBLÉ. Aucune route ne supprime ni ne désactive un lien de partage : une fois partagé, un
 * fichier l'est DÉFINITIVEMENT, et la seule façon de le dépublier est de le SUPPRIMER. Publier et
 * détruire ne devraient pas être la même décision. Un lien est une CAPACITÉ distribuée ; une
 * capacité qu'on ne peut pas reprendre est un défaut de conception, pas une simplification.
 *
 * La cible est désignée comme à la création ({@link createShareRequestSchema} : `targetKind` +
 * `targetId`), et non par le slug — l'appelant révoque « le partage de CE fichier », il n'a pas
 * forcément le slug sous la main, et c'est la même paire que celle qu'il a utilisée pour partager.
 *
 * IDEMPOTENT côté serveur : révoquer une cible sans lien actif est un succès silencieux
 * (`revoked: false`), jamais une erreur — l'état visé est atteint dans les deux cas.
 */
export const revokeShareRequestSchema = z.object({
    targetKind: shareTargetKindSchema,
    targetId: z.uuid(),
});
/**
 * Réponse de `POST /api/share/revoke` : `revoked` dit si un lien ACTIF a réellement basculé
 * (`false` = il n'y en avait pas, cf. l'idempotence ci-dessus).
 *
 * ⚠ CE QUI EST RÉVOQUÉ N'EST PAS RECYCLÉ. La ligne `share_links` est CONSERVÉE, `revoked_at`
 * horodaté : le slug ne doit JAMAIS être réattribué, sinon une URL gardée par un tiers rouvrirait un
 * accès à un contenu sans rapport. Un re-partage ultérieur de la même cible frappe donc un slug
 * NEUF — l'ancienne URL reste morte pour toujours. C'est la propriété qui donne son sens à la
 * révocation, et la raison pour laquelle la migration ajoute des index d'unicité restreints aux
 * liens actifs plutôt que de supprimer la ligne.
 */
export const revokeShareResultSchema = z.object({
    revoked: z.boolean(),
});
/**
 * Query de `GET /api/share/links` (LOT 3) — la SURFACE PUBLIQUE d'une cible : ses liens de partage,
 * actifs ET révoqués, du plus récent au plus ancien.
 *
 * MANQUE COMBLÉ, jumeau de la révocation. Le dépôt n'avait aucun moyen de savoir CE QU'IL PUBLIE :
 * un lien créé à l'upload, un autre minté depuis une page publique, un troisième par un re-partage —
 * rien ne les listait. On ne peut pas reprendre une capacité qu'on ne voit pas, et « révoquer » sans
 * inventaire suppose de connaître d'avance ce qu'on cherche à fermer.
 *
 * La cible est désignée comme à la création/révocation (`targetKind` + `targetId`), pour que les
 * trois routes s'appellent avec la même paire.
 */
export const shareLinksQuerySchema = z.object({
    targetKind: shareTargetKindSchema,
    targetId: z.uuid(),
});
/**
 * Réponse de `GET /api/share/links` : les liens de la cible. Chaque entrée est un
 * {@link shareLinkSchema} dont `revokedAt` est DÉSORMAIS RENSEIGNÉ (`null` = actif).
 *
 * L'historique révoqué est rendu VOLONTAIREMENT : sans lui, « jamais partagé » et « partagé puis
 * dépublié » sont indiscernables. Les slugs révoqués restent affichés parce qu'ils ne seront jamais
 * réattribués — savoir qu'une URL a circulé fait partie de ce qu'on veut pouvoir constater.
 *
 * GARDE : la même capacité que la publication (cf. `POST /api/share`) — un espace personnel
 * d'autrui répond 404, un conteneur mutualisé non gérable répond 403.
 */
export const shareLinksListSchema = z.object({
    links: z.array(shareLinkSchema),
});
/**
 * Métadonnées PUBLIQUES d'un fichier partagé (résolution `GET /api/share/f/:slug`).
 * `expiresAt` = échéance TTL ISO (`null` si `forever`). Le contenu se télécharge via
 * `GET /api/share/f/:slug/content`. La clé de stockage n'est jamais exposée.
 */
export const publicFileSchema = z.object({
    name: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    expiresAt: z.string().nullable(),
});
/** Une entrée fichier dans un dossier partagé (téléchargeable par son id, scoping serveur). */
export const publicFolderFileSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    expiresAt: z.string().nullable(),
    /**
     * Une VIGNETTE dérivée a-t-elle été générée pour ce fichier (colonne `files.has_thumbnail`) ?
     * XFILE-29 — MIROIR de {@link publicFileSchema}/`fileNodeSchema`. OPTIONNEL (additif, garde les
     * lots web verts) : la page PUBLIQUE d'un dossier partagé s'en sert pour décider l'inclusion en
     * GALERIE (une image apparaît ssi nativement affichable OU pourvue d'une vignette) et pour
     * afficher la vignette via `shareFolderFileThumbnailPath`.
     */
    hasThumbnail: z.boolean().optional(),
});
/**
 * Contenu PUBLIC d'un dossier partagé (résolution `GET /api/share/d/:slug`) : le nom
 * du dossier et la LISTE PLATE de ses fichiers actifs. Chaque fichier se télécharge
 * via `GET /api/share/d/:slug/f/:fileId/content` (le serveur vérifie que le fichier
 * appartient bien au dossier partagé et est encore vivant). Périmètre MVP : les
 * sous-dossiers ne sont pas exposés (un lot d'upload est plat).
 */
export const publicFolderSchema = z.object({
    name: z.string(),
    files: z.array(publicFolderFileSchema),
});
// --- Constructeurs de chemins (source unique front + back) -----------------
/** Route SPA publique d'un fichier partagé. */
export function shareFileHref(slug) {
    return `/f/${slug}`;
}
/** Route SPA publique d'un dossier partagé. */
export function shareFolderHref(slug) {
    return `/d/${slug}`;
}
/** Chemin API de résolution des métadonnées d'un fichier partagé. */
export function shareFilePath(slug) {
    return `/api/share/f/${slug}`;
}
/** Chemin API de téléchargement du contenu d'un fichier partagé (browser-natif). */
export function shareFileContentPath(slug) {
    return `/api/share/f/${slug}/content`;
}
/** Chemin API de résolution du contenu d'un dossier partagé. */
export function shareFolderPath(slug) {
    return `/api/share/d/${slug}`;
}
/** Chemin API de téléchargement d'un fichier DANS un dossier partagé (browser-natif). */
export function shareFolderFileContentPath(slug, fileId) {
    return `/api/share/d/${slug}/f/${fileId}/content`;
}
/**
 * Chemin API de la VIGNETTE d'un fichier DANS un dossier partagé (XFILE-29 ; galerie/visionneuse
 * de la page publique). `size` ∈ { sm, md, lg } — `lg` (1600 px) sert la visionneuse. PUBLIC (aucune
 * auth) : la route retombe sur une icône SVG si le fichier n'a pas de vignette (jamais un 404 cassant).
 */
export function shareFolderFileThumbnailPath(slug, fileId, size) {
    return `/api/share/d/${slug}/f/${fileId}/thumbnail/${size}`;
}
/** Chemin API de TÉLÉCHARGEMENT ZIP d'un dossier partagé (browser-natif ; XFILE-29). */
export function shareFolderArchivePath(slug) {
    return `/api/share/d/${slug}/archive.zip`;
}
/** Chemin API d'ESTIMATION (`{ count, totalBytes }`) du ZIP d'un dossier partagé (XFILE-29). */
export function shareFolderArchiveEstimatePath(slug) {
    return `/api/share/d/${slug}/archive/estimate`;
}
/**
 * Chemin API de PRÉPARATION (`POST`) d'un ZIP de SÉLECTION sur la page publique d'un dossier
 * partagé (XFILE-34). Corps = `bulkArchiveRequestSchema` (`{ fileIds, folderIds }`), réponse =
 * `bulkArchivePrepareResultSchema` (`{ token }`). PUBLIC (cf. `isPublicPath`, forme STRICTE) : la
 * page `/d/:slug` est consultée DÉCONNECTÉ. Le serveur vérifie que CHAQUE id appartient au
 * sous-arbre du dossier du slug — jamais de fuite hors du dossier partagé. Le jeton obtenu se
 * passe ensuite en `?token=` à {@link shareFolderArchivePath} / {@link shareFolderArchiveEstimatePath},
 * qui portent alors sur la SÉLECTION au lieu du dossier entier.
 */
export function shareFolderArchivePreparePath(slug) {
    return `/api/share/d/${slug}/archive/prepare`;
}
/**
 * Chemin API de MINT (idempotent) d'un vrai lien court `/f/<slug>` pour un fichier d'un dossier
 * partagé (XFILE-29, `POST`). PUBLIC : un visiteur anonyme d'un dossier partagé peut forger le lien
 * de partage d'un fichier du dossier ; renvoie un {@link shareLinkSchema} (`targetKind: 'file'`).
 */
export function shareFolderFileLinkPath(slug, fileId) {
    return `/api/share/d/${slug}/f/${fileId}/link`;
}
//# sourceMappingURL=share.js.map